import { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import CommonModal, {
  CommonModalFooter,
} from "../shared/modalLayout/CommonModal";
import Button from "../shared/buttons/button";
import { ChevronDownSvg } from "../../svgFiles/ChevronDownSvg";
import {
  AUDIOLOGIST_PRESCRIPTION_TYPE_OPTIONS,
  AUDIOLOGIST_LENS_TYPE_OPTIONS,
  AUDIOLOGIST_INDEX_OPTIONS,
  AUDIOLOGIST_MOUNTING_OPTIONS,
} from "../../mocks/va/mockData";
import {
  buildLensSelectionDraft,
  getPrescriptionEntry,
  getPrescriptionLensCount,
  normalizeLensItems,
} from "../../helpers/prescriptionFormat";
import { createLensSelectionsFromBundle } from "../../helpers/audiologistLensSelection";
import {
  hasBlockingPrescriptionIssue,
  issuesForLensType,
} from "../../helpers/prescriptionExtract";
import { PrescriptionExtractIssues } from "./PrescriptionExtractCompare";
import "../../styles/selectLensesModal.css";
import "../../styles/vaOrderModal.css";

function getOptionLabel(options, value) {
  return options.find((option) => option.value === value)?.label || "";
}

function LensOptionCards({ options, name, value, onSelect }) {
  return (
    <div className="lens-option-cards">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`lens-option-card${
            value === option.value ? " lens-option-card--selected" : ""
          }`}
          onClick={() => onSelect(option.value)}
        >
          <Form.Check
            type="radio"
            id={`${name}-${option.value}`}
            name={name}
            checked={value === option.value}
            onChange={() => onSelect(option.value)}
            label={
              <span className="lens-option-card__content">
                <span className="lens-option-card__title">{option.label}</span>
                {option.description && (
                  <span className="lens-option-card__description">
                    {option.description}
                  </span>
                )}
              </span>
            }
            className="lens-option-card__radio"
          />
        </button>
      ))}
    </div>
  );
}

function LensPickerField({
  label,
  placeholder,
  value,
  options,
  activeField,
  fieldKey,
  onToggle,
  hasError,
  children,
}) {
  const isOpen = activeField === fieldKey;
  const displayLabel = getOptionLabel(options, value) || placeholder;

  return (
    <div className="select-lenses-field">
      <label className="cmn_label d-block">{label}</label>
      <button
        type="button"
        className={`lens-picker-trigger w-100${
          isOpen ? " lens-picker-trigger--open" : ""
        }${hasError ? " lens-picker-trigger--error" : ""}`}
        onClick={() => onToggle(fieldKey)}
      >
        <span
          className={
            value ? "lens-picker-trigger__value" : "lens-picker-trigger__placeholder"
          }
        >
          {displayLabel}
        </span>
        <ChevronDownSvg />
      </button>
      {isOpen && children}
    </div>
  );
}

function createInitialSelections(lensCount, initialSelection, lensItems) {
  return createLensSelectionsFromBundle({
    lensItems,
    lensCount,
    initialSelection,
  });
}

function LensSelectionForm({
  lensIndex,
  lensLabel,
  selection,
  errors,
  extractIssues,
  activeField,
  onToggleField,
  onUpdateField,
  isMultiLens,
}) {
  const fieldPrefix = isMultiLens ? `lens-${lensIndex}-` : "";

  const scopedFieldKey = (field) => `${fieldPrefix}${field}`;

  return (
    <div
      className={`select-lenses-form${
        isMultiLens ? " select-lenses-form--section" : ""
      }`}
    >
      {lensLabel ? (
        <h3 className="select-lenses-form__lens-title">{lensLabel}</h3>
      ) : null}

      <PrescriptionExtractIssues issues={extractIssues} />

      <LensPickerField
        label="Prescription type"
        placeholder="Select Prescription Type"
        value={selection.prescriptionType}
        options={AUDIOLOGIST_PRESCRIPTION_TYPE_OPTIONS}
        activeField={activeField}
        fieldKey={scopedFieldKey("prescriptionType")}
        onToggle={onToggleField}
        hasError={errors.prescriptionType}
      >
        <LensOptionCards
          options={AUDIOLOGIST_PRESCRIPTION_TYPE_OPTIONS}
          name={`${fieldPrefix}prescriptionType`}
          value={selection.prescriptionType}
          onSelect={(value) => onUpdateField(lensIndex, "prescriptionType", value)}
        />
      </LensPickerField>

      <div className="select-lenses-row">
        <LensPickerField
          label="Lens Type"
          placeholder="Select Lens Type"
          value={selection.lensType}
          options={AUDIOLOGIST_LENS_TYPE_OPTIONS}
          activeField={activeField}
          fieldKey={scopedFieldKey("lensType")}
          onToggle={onToggleField}
          hasError={errors.lensType}
        >
          <LensOptionCards
            options={AUDIOLOGIST_LENS_TYPE_OPTIONS}
            name={`${fieldPrefix}lensType`}
            value={selection.lensType}
            onSelect={(value) => onUpdateField(lensIndex, "lensType", value)}
          />
        </LensPickerField>

        <LensPickerField
          label="Index"
          placeholder="Select Index Type"
          value={selection.indexType}
          options={AUDIOLOGIST_INDEX_OPTIONS}
          activeField={activeField}
          fieldKey={scopedFieldKey("indexType")}
          onToggle={onToggleField}
          hasError={errors.indexType}
        >
          <LensOptionCards
            options={AUDIOLOGIST_INDEX_OPTIONS}
            name={`${fieldPrefix}indexType`}
            value={selection.indexType}
            onSelect={(value) => onUpdateField(lensIndex, "indexType", value)}
          />
        </LensPickerField>
      </div>

      <LensPickerField
        label="Mounting"
        placeholder="Select Mount Type"
        value={selection.mountingType}
        options={AUDIOLOGIST_MOUNTING_OPTIONS}
        activeField={activeField}
        fieldKey={scopedFieldKey("mountingType")}
        onToggle={onToggleField}
        hasError={errors.mountingType}
      >
        <LensOptionCards
          options={AUDIOLOGIST_MOUNTING_OPTIONS}
          name={`${fieldPrefix}mountingType`}
          value={selection.mountingType}
          onSelect={(value) => onUpdateField(lensIndex, "mountingType", value)}
        />
      </LensPickerField>
    </div>
  );
}

export default function SelectLensesModal({
  show,
  onClose,
  onBack,
  onConfirm,
  initialSelection,
  onSelectionChange,
  prescription,
  prescriptionSkipped = false,
  lensItems = [],
}) {
  const hasHydratedRef = useRef(false);
  const normalizedLensItems = useMemo(
    () => normalizeLensItems(lensItems),
    [lensItems]
  );
  const lensCount = getPrescriptionLensCount(normalizedLensItems);
  const isMultiLens = lensCount > 1;

  const [selections, setSelections] = useState(() =>
    createInitialSelections(
      lensCount,
      initialSelection,
      normalizedLensItems
    )
  );
  const [activeField, setActiveField] = useState(null);
  const [errorsMap, setErrorsMap] = useState({});

  useEffect(() => {
    if (!show) {
      hasHydratedRef.current = false;
      return;
    }
    if (hasHydratedRef.current) return;
    hasHydratedRef.current = true;
    const nextSelections = createInitialSelections(
      lensCount,
      initialSelection,
      normalizedLensItems
    );
    setSelections(nextSelections);
    onSelectionChange?.(
      buildLensSelectionDraft({
        lensItems: normalizedLensItems,
        selections: nextSelections,
      })
    );
    setErrorsMap({});
    setActiveField(null);
  }, [show, initialSelection, lensCount, normalizedLensItems, onSelectionChange]);

  const extractIssuesByIndex = useMemo(() => {
    if (prescriptionSkipped || !prescription) return {};

    return Object.fromEntries(
      selections.map((selection, index) => {
        const entry =
          getPrescriptionEntry(prescription, index, isMultiLens) ||
          prescription;
        return [
          index,
          issuesForLensType(
            entry,
            selection.prescriptionType,
            entry?.extractMeta || prescription.extractMeta || {}
          ),
        ];
      })
    );
  }, [prescription, prescriptionSkipped, selections, isMultiLens]);

  const blockingExtract = hasBlockingPrescriptionIssue(
    Object.values(extractIssuesByIndex).flat()
  );

  const persistDraft = (nextSelections) => {
    onSelectionChange?.(
      buildLensSelectionDraft({
        lensItems: normalizedLensItems,
        selections: nextSelections,
      })
    );
  };

  const updateField = (lensIndex, field, value) => {
    setSelections((prev) => {
      const next = prev.map((entry, index) =>
        index === lensIndex ? { ...entry, [field]: value } : entry
      );
      persistDraft(next);
      return next;
    });
    setErrorsMap((prev) => ({
      ...prev,
      [lensIndex]: { ...(prev[lensIndex] || {}), [field]: false },
    }));
    setActiveField(null);
  };

  const toggleField = (fieldKey) => {
    setActiveField((current) => (current === fieldKey ? null : fieldKey));
  };

  const validateSelection = (selection) => {
    const nextErrors = {};
    if (!selection.prescriptionType) nextErrors.prescriptionType = true;
    if (!selection.lensType) nextErrors.lensType = true;
    if (!selection.indexType) nextErrors.indexType = true;
    if (!selection.mountingType) nextErrors.mountingType = true;
    return nextErrors;
  };

  const validate = () => {
    const nextErrorsMap = {};
    let isValid = true;

    selections.forEach((selection, index) => {
      const entryErrors = validateSelection(selection);
      if (Object.keys(entryErrors).length > 0) {
        nextErrorsMap[index] = entryErrors;
        isValid = false;
      }
    });

    setErrorsMap(nextErrorsMap);
    return isValid;
  };

  const handleConfirm = () => {
    if (!validate()) return;
    if (blockingExtract) return;
    onConfirm(
      buildLensSelectionDraft({
        lensItems: normalizedLensItems,
        selections,
      })
    );
  };

  const handleBack = () => {
    persistDraft(selections);
    onBack?.();
  };

  return (
    <CommonModal
      show={show}
      setShow={(open) => {
        if (!open) onClose();
      }}
      title="Select the lenses"
      centered
      className={`select-lenses-modal${
        isMultiLens ? " select-lenses-modal--multi" : ""
      } modal-md`}
      onHide={onClose}
      onBack={handleBack}
      closeOnBackdrop={false}
      footer={
        <CommonModalFooter className="select-lenses-footer d-flex gap-3 mt-4">
          <Button
            label="Confirm"
            onClick={handleConfirm}
            disabled={blockingExtract}
          />
          <Button label="Cancel" className="cancel" onClick={onClose} />
        </CommonModalFooter>
      }
    >
      <div className="select-lenses-modal__sections">
        {selections.map((selection, index) => (
          <LensSelectionForm
            key={index}
            lensIndex={index}
            lensLabel={
              isMultiLens
                ? normalizedLensItems[index] || `Lens ${index + 1}`
                : null
            }
            selection={selection}
            errors={errorsMap[index] || {}}
            extractIssues={extractIssuesByIndex[index] || []}
            activeField={activeField}
            onToggleField={toggleField}
            onUpdateField={updateField}
            isMultiLens={isMultiLens}
          />
        ))}
      </div>
    </CommonModal>
  );
}
